// Called from tuple wrapper (or itself) to handle simple values inside of a tuple (or handle sub tuples)
import type { FC } from "react"
import React, { useEffect } from "react"
import { useFormik } from "formik"
import {
  FormLabel,
  HStack,
  Input,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react"
import type { AbiInput } from "web3-utils"

import {
  encodeComplexField,
  encodeSimpleField,
} from "createProposal/helpers/abi"
import { CREATE_PROPOSAL_ERRORS } from "errors/createProposal"
import CircleWavesIcon from "ui/components/icons/CircleWavesIcon"

type SimpleValueInputProps = {
  components: AbiInput[]
  changeInputValue: (newValue: any, name?: string) => void
  inputName?: string
  shouldIndentLabel?: boolean
  indexInArray?: number
}

type FormValues = {
  [key: string]: string
}

const SimpleValueInput: FC<SimpleValueInputProps> = ({
  components,
  changeInputValue,
  inputName,
  shouldIndentLabel,
  indexInArray,
}) => {
  const initialValues = components.reduce<FormValues>((acc, component) => {
    return { ...acc, [component.name]: "" }
  }, {})

  const validate = (values: FormValues): Record<string, string> => {
    const errors: Record<string, string> = {}

    Object.keys(values).forEach((key) => {
      const component = components.find((c) => c.name === key)

      if (component) {
        try {
          if (component.type === "tuple" || component.type.endsWith("[]"))
            encodeComplexField(
              component.components,
              component.type,
              values[key],
            )
          else encodeSimpleField(component.type, values[key])

          if (values[key].length < 1) {
            // no value
            errors[key] =
              CREATE_PROPOSAL_ERRORS.actions.method.invalidArgs().displayValue
          }
        } catch (error) {
          errors[key] =
            CREATE_PROPOSAL_ERRORS.actions.method.invalidArgs().displayValue
        }
      }
    })

    return errors
  }

  const formik = useFormik({
    initialValues: initialValues,
    validate,
    onSubmit: () => {
      // do nothing, values are updating the main form on the go
    },
  })

  // whenever it's changed, we recombine the values so it's seamless for the user
  useEffect(() => {
    const touchedAnyField = Object.keys(formik.touched).length > 0

    if (touchedAnyField) {
      if (formik.isValid) {
        const combinedValuesList = Object.keys(formik.values).map((key) => {
          const component = components.find((c) => c.name === key)

          if (component) {
            if (
              ["string", "address", "byte", "int"].some((word) =>
                component.type.includes(word),
              )
            ) {
              return `"${formik.values[key]}"`
            } else {
              return `${formik.values[key]}`
            }
          }

          return null // Return null for values without a matching component.
        })

        const combinedValues = `[${combinedValuesList.join(", ")}]`

        changeInputValue(combinedValues, inputName)
      } else {
        const combinedValues = ""

        changeInputValue(combinedValues, inputName)
      }
    }
    // adding changeInputValue will freeze the app
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, formik.isValid, components, inputName, formik.touched])

  return (
    <Stack gap={0}>
      {components.map((component, index) => {
        const placeholderText =
          component.type === "tuple" && component.components
            ? `tuple[${component.components
                ?.map(({ type }) => type)
                .join(", ")}]`
            : component.type

        const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
          if (placeholderText !== "string") {
            const trimmedValue = e.target.value.trim()

            await formik.setFieldValue(component.name, trimmedValue, true)

            formik.handleBlur(e)

            formik.validateForm()
          }
        }

        return (
          <Stack key={component.name}>
            <HStack h={10}>
              {shouldIndentLabel ? (
                <Tooltip
                  fontSize="md"
                  label={`Tuple with index ${indexInArray} in array`}
                >
                  <Text
                    alignItems="center"
                    color="gray.500"
                    display="flex"
                    h="full"
                    justifyContent="center"
                    m={0}
                    w={41}
                  >
                    {index === 0 ? indexInArray : <CircleWavesIcon />}
                  </Text>
                </Tooltip>
              ) : undefined}
              <FormLabel
                alignItems="center"
                bg="purple.50"
                border="gray.dark"
                color="purple.600"
                display="flex"
                h="full"
                justifyContent="center"
                m={0}
                p={1}
                w={shouldIndentLabel ? 165 : 220}
              >
                {component.name}
              </FormLabel>
              <Input
                isInvalid={
                  !!formik.errors[component.name] &&
                  formik.touched[component.name]
                }
                name={component.name}
                placeholder={placeholderText}
                type="text"
                value={formik.values[component.name]}
                onBlur={handleBlur}
                onChange={formik.handleChange}
              />
            </HStack>
          </Stack>
        )
      })}
    </Stack>
  )
}

export default SimpleValueInput
